// Client-side overlap detection for the schedule form's live conflict hint.
// Approximate but deterministic: it mirrors the resolver's tie-break so the hint
// matches what the player will actually do. Pure + testable.

import { TARGET_SPECIFICITY, type TargetType } from '@seencast/shared';

export interface ConflictWindow {
  id?: string;
  days_mask: number;
  start_time: string | null;
  end_time: string | null;
  start_date: string | null;
  end_date: string | null;
  priority: number;
  target_type: TargetType;
  // Screen ids this schedule affects (screen → [self]; group → members; org → all).
  screens: Set<string>;
  playlist_name?: string;
  created_at?: number;
}

function daysOverlap(a: number, b: number): boolean {
  return (a & b) !== 0;
}

function timeOverlap(aS: string | null, aE: string | null, bS: string | null, bE: string | null): boolean {
  // An overnight window (start > end) wraps midnight; treat conservatively as overlapping.
  const overnight = (s: string | null, e: string | null) => !!(s && e && s > e);
  if (overnight(aS, aE) || overnight(bS, bE)) return true;
  const as = aS ?? '00:00';
  const ae = aE ?? '24:00';
  const bs = bS ?? '00:00';
  const be = bE ?? '24:00';
  return as < be && bs < ae;
}

function dateOverlap(aS: string | null, aE: string | null, bS: string | null, bE: string | null): boolean {
  if (aE && bS && aE < bS) return false;
  if (bE && aS && bE < aS) return false;
  return true;
}

function screensIntersect(a: Set<string>, b: Set<string>): boolean {
  for (const s of a) if (b.has(s)) return true;
  return false;
}

export interface ConflictResult {
  competitor: ConflictWindow;
  thisWins: boolean;
  reason: 'priority' | 'specificity' | 'newer';
}

// The minimal shape the resolver tie-break needs. Both the form's conflict hint
// and the week calendar's winner marking rank windows through this, so there is a
// single source of truth for docs/02 §Resolver step 3.
export interface RankWindow {
  priority: number;
  target_type: TargetType;
  created_at?: number;
}

/**
 * Resolver tie-break (docs/02 step 3): higher priority wins; on a tie the more
 * specific target wins (screen > group > org); on a further tie the newer
 * created_at wins. Returns > 0 when `a` beats `b`, < 0 when `b` beats `a`, and 0
 * when they are truly indistinguishable. This mirrors `pickWinner` in the API
 * resolver and `beats` in the mock resolver — keep the three in agreement.
 */
export function compareForWin(a: RankWindow, b: RankWindow): number {
  if (a.priority !== b.priority) return a.priority - b.priority;
  const sa = TARGET_SPECIFICITY[a.target_type];
  const sb = TARGET_SPECIFICITY[b.target_type];
  if (sa !== sb) return sa - sb;
  return (a.created_at ?? 0) - (b.created_at ?? 0);
}

/**
 * Given a proposed schedule window and the existing ones, return the single most
 * relevant overlap (highest-priority competitor), or null if there is none.
 */
export function findTopConflict(
  proposed: ConflictWindow,
  existing: ConflictWindow[],
): ConflictResult | null {
  const overlaps = existing.filter(
    (e) =>
      // Exclude the schedule being edited (only when it actually has an id).
      !(proposed.id !== undefined && e.id === proposed.id) &&
      screensIntersect(proposed.screens, e.screens) &&
      daysOverlap(proposed.days_mask, e.days_mask) &&
      timeOverlap(proposed.start_time, proposed.end_time, e.start_time, e.end_time) &&
      dateOverlap(proposed.start_date, proposed.end_date, e.start_date, e.end_date),
  );
  if (overlaps.length === 0) return null;

  // Pick the competitor most likely to actually contend (the resolver's own winner
  // among the overlaps), ranked by the shared tie-break.
  const competitor = overlaps.reduce((best, cur) => (compareForWin(cur, best) > 0 ? cur : best));

  // A brand-new (unsaved) schedule is the newest, so it wins any created_at tie.
  const proposedRank: RankWindow = {
    priority: proposed.priority,
    target_type: proposed.target_type,
    created_at: proposed.created_at ?? Number.POSITIVE_INFINITY,
  };
  const thisWins = compareForWin(proposedRank, competitor) >= 0;
  const reason: ConflictResult['reason'] =
    proposed.priority !== competitor.priority
      ? 'priority'
      : TARGET_SPECIFICITY[proposed.target_type] !== TARGET_SPECIFICITY[competitor.target_type]
        ? 'specificity'
        : 'newer';
  return { competitor, thisWins, reason };
}
